import { CommonUI } from "@src/common/CommonUI/CommonUI";
import { IllustrationBG } from "@src/common/IllustrationBG/IllustrationBG";
import { LoadingOverlay } from "@src/common/LoadingOverlay";
import { PageLayout } from "@src/common/PageLayout";
import { APP_ROUTES } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { useInteractionGuard } from "@src/context/InteractionGuardContext";
import { useThemeContext } from "@src/context/ThemeContext";
import { useEntryRouteBootstrap } from "@src/hooks/useEntryRouteBootstrap";
import { useThemePageContent } from "@src/hooks/useThemePageText";
import { fetchPartners } from "@src/services/partners.service";
import logger from "@src/utils/logger";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { useLocation, useParams } from "@uhg-abyss/web/tools/reactRouterTools";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { NavButtons } from "./NavButtons";
import { NavButtonsLegacy } from "./NavButtonsLegacy";
import { ReferForm } from "./ReferForm";
import { ReferFormLegacy } from "./ReferFormLegacy";
import { consumeReferThemeChangeInProgress } from "./themeChangeSignal";

const ReferContent = styled("div", {
  height: "auto !important",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "start",
  width: "100%",
});

const DEFAULT_REFER_PAGE_TEXT = "Welcome to the referral portal for Optum Behavioral Care (OBC)";

export const Refer = () => {
  const { data, setData, clearSession } = useApplicationContext();
  const { clearBranding, setBranding } = useThemeContext();
  const { FEATURE_UPDATED_REFER_PAGE } = useFeatureFlags();
  const { parentOrganization: pathParentOrg } = useParams();
  const referLocation = useLocation();
  const parentOrganization = FEATURE_UPDATED_REFER_PAGE
    ? pathParentOrg ||
      new URLSearchParams(referLocation.search).get("parentOrganization") ||
      undefined
    : pathParentOrg;
  const [isInitialized, setIsInitialized] = useState(false);
  const hasRunInitializationRef = useRef(false);
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const getThemePageContent = useThemePageContent();
  const { isActionPending, isUiBlocked } = useInteractionGuard();

  const { headingText, metaTitle } = getThemePageContent("refer", {
    headingText: DEFAULT_REFER_PAGE_TEXT,
    metaTitle: DEFAULT_REFER_PAGE_TEXT,
  });

  useEffect(() => {
    if (hasRunInitializationRef.current) {
      return;
    }

    let isCancelled = false;

    if (consumeReferThemeChangeInProgress()) {
      // Theme change triggered a remount — skip clearing to preserve partner/referrer-team selections
      setData("isReferrerFlow", true);
      hasRunInitializationRef.current = true;
      setIsInitialized(true);
      return;
    }

    if (data.entryRouteIdentityPath !== APP_ROUTES.REFER) {
      clearSession(); // Direct route mounts outside the root bootstrap still start fresh.
    }

    if (!parentOrganization) {
      clearBranding(); // Clear branding to prevent branded experience bleed from other routes
      setData("isReferrerFlow", true);
      hasRunInitializationRef.current = true;
      setIsInitialized(true);
      return;
    }

    // Pre-resolve partner branding before rendering to prevent a flash from Optum to partner branding
    const initializeRefer = async () => {
      try {
        const partners = await fetchPartners(parentOrganization, data.userUuid);

        if (isCancelled) {
          return;
        }

        if (Array.isArray(partners) && partners.length === 1) {
          setBranding(partners[0].branding);
        } else {
          clearBranding();
        }
      } finally {
        if (!isCancelled) {
          setData("isReferrerFlow", true);
          hasRunInitializationRef.current = true;
          setIsInitialized(true);
        }
      }
    };

    initializeRefer();

    return () => {
      isCancelled = true;
    };
  }, [
    clearSession,
    setData,
    clearBranding,
    setBranding,
    parentOrganization,
    data.entryRouteIdentityPath,
    data.userUuid,
  ]);

  useEntryRouteBootstrap({
    initReady: isInitialized,
    isReferrerFlow: data.isReferrerFlow ?? true,
    onUserUuidCreated: () => {
      logger.log({ text: "Generated new user UUID" });
    },
    digitalInfoLogLocation: "Refer",
  });

  const renderReferForm = useCallback(() => {
    if (!isInitialized) return null;
    return FEATURE_UPDATED_REFER_PAGE ? (
      <ReferForm isMobile={isMobile} />
    ) : (
      <ReferFormLegacy isMobile={isMobile} />
    );
  }, [isMobile, isInitialized, FEATURE_UPDATED_REFER_PAGE]);

  if (!isInitialized) {
    return <LoadingOverlay isLoading={true} ariaLoadingLabel="Loading referral page..." />;
  }

  return (
    <Fragment>
      <PageLayout css={{ paddingBottom: isMobile ? "0" : "50px" }}>
        <IllustrationBG />
        <ReferContent
          css={FEATURE_UPDATED_REFER_PAGE ? { width: "100%" } : undefined}
          data-testid="test-id-refer-content"
        >
          <CommonUI
            headingText={headingText}
            metaTitle={metaTitle}
            bodyText={null}
            FormComponent={renderReferForm}
            formMaxWidth={FEATURE_UPDATED_REFER_PAGE ? "860px" : undefined}
          />
          {FEATURE_UPDATED_REFER_PAGE ? (
            <NavButtons
              isMobile={isMobile}
              isSubmitting={
                isActionPending("refer:schedule-now") ||
                isActionPending("refer:request-callback") ||
                !!data.isReferFormLoading
              }
              isUiBlocked={isUiBlocked}
            />
          ) : (
            <NavButtonsLegacy
              isMobile={isMobile}
              isSubmitting={
                isActionPending("refer:schedule-now") || isActionPending("refer:request-callback")
              }
              isUiBlocked={isUiBlocked}
            />
          )}
        </ReferContent>
      </PageLayout>
    </Fragment>
  );
};
